// Settings Panel Component
// Application settings and preferences
// Architecture: v4.0.0

import { devWarn } from '../../../utils/devLog';
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Switch,
  IconButton,
  Divider,
  Tooltip,
  Select
} from '@chakra-ui/react';
import { SettingsIcon } from '@chakra-ui/icons';
import { DisplayModeSettings } from './DisplayModeSettings';
import { globalEventStream } from './universal_architecture/core/EventStream';
import {
  DATE_TIME_FORMATS,
  getSavedDateTimeFormat,
  saveDateTimeFormat
} from '../../../config/dateTimeFormats';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

/**
 * Settings Controls Section
 * App-wide settings and preferences
 */
const STORAGE_KEY_SHOW_LOCATION = 'hora-showLocation';

const SettingsControlsSection: React.FC = () => {
  // Date Time Format setting
  const [dateTimeFormatId, setDateTimeFormatId] = useState(() => {
    return getSavedDateTimeFormat().id;
  });

  // Show Location setting - controls visibility of Location field in Chart Info
  const [showLocation, setShowLocation] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_SHOW_LOCATION);
      // Default to true if not set
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });

  // Handle Show Location toggle - save to localStorage, update global flag, emit event
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SHOW_LOCATION, String(showLocation));
      window.globalShowLocation = showLocation;
    } catch (error) {
      devWarn('Failed to save showLocation to localStorage:', error);
    }

    // Emit event for reactive update of ChartInfoSection
    globalEventStream.emit({
      id: `location-visibility-changed-${Date.now()}`,
      type: 'settings.location.changed',
      source: 'settings-panel',
      timestamp: Date.now(),
      data: { showLocation }
    });
  }, [showLocation]);

  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
        Application Settings
      </Text>

      <VStack align="stretch" spacing={2}>
        {/* Show Location Toggle */}
        <HStack justify="space-between">
          <VStack align="start" spacing={0}>
            <Text fontSize="sm">Show Location</Text>
            <Text fontSize="xs" color="gray.500">Display coordinates in Chart Info</Text>
          </VStack>
          <Switch
            size="sm"
            isChecked={showLocation}
            onChange={(e) => setShowLocation(e.target.checked)}
            colorScheme="teal"
          />
        </HStack>

        {/* Date Time Format Dropdown */}
        <HStack justify="space-between">
          <VStack align="start" spacing={0}>
            <Text fontSize="sm">Date Time Format</Text>
            <Text fontSize="xs" color="gray.500">For new chart names</Text>
          </VStack>
          <Select
            size="sm"
            width="220px"
            value={dateTimeFormatId}
            onChange={(e) => {
              const newFormatId = e.target.value;
              setDateTimeFormatId(newFormatId);
              saveDateTimeFormat(newFormatId);
            }}
          >
            {DATE_TIME_FORMATS.map((format) => (
              <option key={format.id} value={format.id}>
                {format.example}
              </option>
            ))}
          </Select>
        </HStack>
      </VStack>
    </VStack>
  );
};

/**
 * Main Settings Panel Component
 * Modal dialog for settings and debug info
 */
export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  isOpen,
  onClose,
  className
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <SettingsIcon color="blue.500" />
            <Text>Application Settings</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={6}>
            <DisplayModeSettings />
            <Divider />
            <SettingsControlsSection />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

/**
 * Settings Toggle Button
 * Button to open the settings panel
 */
interface SettingsToggleProps {
  onClick: () => void;
  isActive?: boolean;
  className?: string;
}

export const SettingsToggle: React.FC<SettingsToggleProps> = ({
  onClick,
  isActive = false,
  className
}) => {
  return (
    <Tooltip label="Application Settings" hasArrow>
      <IconButton
        className={className}
        aria-label="Open settings"
        icon={<SettingsIcon />}
        size="sm"
        variant={isActive ? 'solid' : 'ghost'}
        colorScheme={isActive ? 'blue' : 'gray'}
        onClick={onClick}
        height="32px"
        minWidth="32px"
        _hover={{ bg: isActive ? 'blue.600' : 'gray.100' }}
        color={isActive ? 'white' : 'gray.600'}
      />
    </Tooltip>
  );
};

export default SettingsPanel;